import { MouseEvent, TouchEvent, useCallback, useRef, useState } from 'react';

type Props = {
	onLongPress: (e: MouseEvent | TouchEvent) => void;
	onClick: (e: MouseEvent | TouchEvent) => void;
	shouldPreventDefault?: boolean;
	delay?: number;
};

export const useLongPress = ({ onLongPress, onClick, shouldPreventDefault = true, delay = 300 }: Props) => {
	const target = useRef<EventTarget>();
	const timeout = useRef<ReturnType<typeof setTimeout>>();
	const [longPressTriggered, setLongPressTriggered] = useState(false);

	const start = useCallback(
		(event: MouseEvent | TouchEvent) => {
			if (shouldPreventDefault && event.target) {
				event.target.addEventListener('touchend', preventDefault, {
					passive: false,
				});
				target.current = event.target;
			}
			timeout.current = setTimeout(() => {
				onLongPress(event);
				setLongPressTriggered(true);
			}, delay);
		},
		[onLongPress, delay, shouldPreventDefault]
	);

	const clear = useCallback(
		(event: MouseEvent | TouchEvent, shouldTriggerClick = true) => {
			if (timeout.current) {
				clearTimeout(timeout.current);
			}
			if (shouldTriggerClick && !longPressTriggered) {
				onClick(event);
			}
			setLongPressTriggered(false);
			if (shouldPreventDefault && target.current) {
				target.current.removeEventListener('touchend', () => null);
			}
		},
		[shouldPreventDefault, onClick, longPressTriggered]
	);

	return {
		onMouseDown: (e: MouseEvent) => start(e),
		onTouchStart: (e: TouchEvent) => start(e),
		onMouseUp: (e: MouseEvent) => clear(e),
		onMouseLeave: (e: MouseEvent) => clear(e, false),
		onTouchEnd: (e: TouchEvent) => clear(e),
	};
};

const isTouchEvent = (event: unknown): event is TouchEvent => {
	return !!event && typeof event === 'object' && 'touches' in event;
};

const preventDefault = (event: Event) => {
	if (!isTouchEvent(event)) return;
	if (event.touches.length < 2 && event.cancelable && event.preventDefault) {
		event.preventDefault();
	}
};
